import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Stack,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Note as NoteIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationCity as LocationIcon,
  Work as WorkIcon,
  FileDownload as DownloadIcon,
} from '@mui/icons-material';
import publicApplicationsService from '../../../services/publicApplicationsService';
import ApplicationDetailModal from '../../../components/ApplicationDetailModal';
import ExportApplicationsModal from '../../../components/ExportApplicationsModal';
import api from '../../../api';

const PublicApplications = () => {
  // State
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalApplications, setTotalApplications] = useState(0);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    job_id: '',
    city: '',
    nationality: '',
    from_date: '',
    to_date: '',
  });

  // Status mapping
  const statusLabels = {
    '': 'All',
    'pending': 'Pending',
    'under_review': 'Under Review',
    'shortlisted': 'Shortlisted',
    'rejected': 'Rejected',
    'on_hold': 'On Hold',
    'consider_other_role': 'Consider Other Role',
    'moved_to_candidate': 'Moved to Candidate',
  };

  const statusColors = {
    'pending': 'warning',
    'under_review': 'info',
    'shortlisted': 'success',
    'rejected': 'error',
    'on_hold': 'default',
    'consider_other_role': 'secondary',
    'moved_to_candidate': 'primary',
  };

  const tabs = [
    { label: 'All', status: '' },
    { label: 'Pending', status: 'pending' },
    { label: 'Under Review', status: 'under_review' },
    { label: 'Shortlisted', status: 'shortlisted' },
    { label: 'Rejected', status: 'rejected' },
    { label: 'On Hold', status: 'on_hold' },
  ];

  // Load stats
  const loadStats = async () => {
    try {
      const response = await publicApplicationsService.getStats();
      if (response.success) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Load applications
  const loadApplications = async () => {
    setLoading(true);
    try {
      const response = await publicApplicationsService.getApplications({
        ...filters,
        page: page + 1,
        per_page: rowsPerPage,
      });

      if (response.success) {
        setApplications(response.data || []);
        setTotalApplications(response.total || 0);
      }
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load jobs
  const loadJobs = async () => {
    try {
      const response = await api.get('/public-applications/jobs');
      const jobsData = response.data.jobs || response.data || [];
      setJobs(Array.isArray(jobsData) ? jobsData : []);
    } catch (error) {
      console.error('Error loading jobs:', error);
      setJobs([]);
    }
  };

  // Effects
  useEffect(() => {
    loadStats();
    loadJobs();
  }, []);

  useEffect(() => {
    loadApplications();
  }, [filters, page, rowsPerPage]);

  // Handlers
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setFilters({ ...filters, status: tabs[newValue].status });
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewApplication = async (id) => {
    try {
      const response = await publicApplicationsService.getApplication(id);
      if (response.success) {
        setSelectedApplication(response.application);
        setDetailModalOpen(true);
      }
    } catch (error) {
      console.error('Error loading application details:', error);
    }
  };

  const handleCloseModal = () => {
    setDetailModalOpen(false);
    setSelectedApplication(null);
  };

  const handleRefresh = () => {
    loadStats();
    loadApplications();
  };

  const handleApplicationUpdated = () => {
    loadStats();
    loadApplications();
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#29204C' }}>
          Job Applications
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => setExportModalOpen(true)}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
          >
            Export to Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ borderColor: '#29204C', color: '#29204C' }}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#f5f5f5' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#29204C', mb: 1 }}>
                {stats.total || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Applications
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00', mb: 1 }}>
                {stats.pending || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Review
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', mb: 1 }}>
                {stats.shortlisted || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Shortlisted
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', mb: 1 }}>
                {stats.under_review || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Under Review
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
            '& .Mui-selected': { color: '#29204C' },
            '& .MuiTabs-indicator': { bgcolor: '#29204C' },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>

        {/* Filters */}
        <Box sx={{ p: 3, bgcolor: '#fafafa' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, email, phone..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Job</InputLabel>
                <Select
                  value={filters.job_id}
                  onChange={(e) => handleFilterChange('job_id', e.target.value)}
                  label="Filter by Job"
                >
                  <MenuItem value="">All Jobs</MenuItem>
                  {jobs.map((job) => (
                    <MenuItem key={job.id || job.job_id} value={job.id || job.job_id}>
                      {job.job_title || job.posting_title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                placeholder="City"
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                placeholder="Nationality"
                value={filters.nationality}
                onChange={(e) => handleFilterChange('nationality', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                InputLabelProps={{ shrink: true }}
                value={filters.from_date}
                onChange={(e) => handleFilterChange('from_date', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                InputLabelProps={{ shrink: true }}
                value={filters.to_date}
                onChange={(e) => handleFilterChange('to_date', e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Applicant</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Position</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Applied Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : applications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">No applications found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                applications.map((app) => (
                  <TableRow key={app.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {app.candidate?.full_name || 'N/A'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="body2">{app.candidate?.email || 'N/A'}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">{app.candidate?.mobile || 'N/A'}</Typography>
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <WorkIcon fontSize="small" color="action" />
                        <Typography variant="body2">{app.job?.job_title || app.job?.posting_title || app.job_title || '-'}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(app.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabels[app.status]}
                        color={statusColors[app.status]}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewApplication(app.id)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalApplications}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[15, 25, 50, 100]}
        />
      </Paper>

      {/* Application Detail Modal */}
      {selectedApplication && (
        <ApplicationDetailModal
          open={detailModalOpen}
          onClose={handleCloseModal}
          application={selectedApplication}
          onApplicationUpdated={handleApplicationUpdated}
        />
      )}

      {/* Export Applications Modal */}
      <ExportApplicationsModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        currentFilters={filters}
        jobs={jobs}
      />
    </Container>
  );
};

export default PublicApplications;
